import { Tag } from "antd";
import dayjs from "dayjs";

import { IProject } from "projects/models";
import { ProjectStages } from "./ProjectStages";
import { utilityService } from "shared/services";
import { ISelectOption } from "shared/models";
import { ColumnType } from "antd/es/table";
import { DateFormat } from "shared/constants";

const getStatusColor = (status?: string) => {
  switch (status?.toLowerCase()) {
    case "in progress":
      return "processing";
    case "complete":
    case "completed":
      return "success";
    case "deficient":
      return "error";
    case "on hold":
      return "warning";
    default:
      return "default";
  }
};

type ProjectColumnRenderType =
  | "text"
  | "upper"
  | "phone"
  | "list"
  | "date"
  | "currency"
  | "number"
  | "boolean"
  | "status"
  | "stage"
  | "utility"
  | "note";

interface ProjectColumnDef {
  key: keyof IProject;
  title: string;
  type: ProjectColumnRenderType;
}

/**
 * The columns shown by default (and their order) when a user has not customized
 * their project list. Every other field is available via the column picker but
 * hidden until the user opts in.
 */
export const DEFAULT_VISIBLE_PROJECT_COLUMNS: string[] = [
  "name",
  "utilityProjectId",
  "projectCode",
  "utilityProgram",
  "status",
  "stage",
  "financeExpirationDate",
  "utilityExpirationDate",
  "utilityEcd",
  "latestProjectNote",
];

// Single source of truth for every selectable project-list column. Default
// columns come first (preserving the historical order); the remaining fields
// follow in IProject declaration order. Labels mirror the project forms.
const PROJECT_COLUMN_DEFS: ProjectColumnDef[] = [
  // --- Default-visible columns (historical order) ---
  { key: "name", title: "Project Name", type: "text" },
  { key: "utilityProjectId", title: "Utility Project ID", type: "text" },
  { key: "projectCode", title: "Project Code", type: "upper" },
  { key: "utilityProgram", title: "Utility", type: "utility" },
  { key: "status", title: "Status", type: "status" },
  { key: "stage", title: "Stage", type: "stage" },
  { key: "financeExpirationDate", title: "Finance Expiration", type: "date" },
  { key: "utilityExpirationDate", title: "Utility Expiration", type: "date" },
  { key: "utilityEcd", title: "Utility ECD", type: "date" },
  { key: "latestProjectNote", title: "Latest Project Note", type: "note" },

  // --- Additional fields (default-hidden) ---
  { key: "projectTypes", title: "Project Type(s)", type: "list" },
  { key: "address", title: "Address", type: "text" },
  { key: "city", title: "City", type: "text" },
  { key: "state", title: "State", type: "text" },
  { key: "zip", title: "Zip", type: "text" },
  { key: "createdDate", title: "Created Date", type: "date" },
  { key: "updatedDate", title: "Updated Date", type: "date" },
  { key: "archived", title: "Archived", type: "boolean" },
  { key: "customerPaymentMethod", title: "Customer Payment Method", type: "text" },
  { key: "ownerOrTenantPayee", title: "Owner or Tenant Payee", type: "text" },
  { key: "utilityBillsReceivedAndReviewed", title: "Utility Bills Received and Reviewed", type: "date" },
  { key: "auditRequestFormReceivedReviewed", title: "Audit Request Form Received/Reviewed", type: "date" },
  { key: "auditScheduleDate", title: "Audit Schedule Date", type: "date" },
  { key: "onsiteAuditDone", title: "Onsite Audit Done", type: "date" },
  { key: "ceToolDone", title: "CE Tool Done", type: "date" },
  { key: "hvacAssessmentFormDone", title: "HVAC Assessment Form Done", type: "date" },
  { key: "hvacPrePhotosReceived", title: "HVAC Pre-photos Received", type: "date" },
  { key: "sentToHcnForQuote", title: "Sent to HCN For Quote", type: "date" },
  { key: "hcnQuotePackageReceived", title: "HCN Quote Package Received", type: "date" },
  { key: "hvacQuoteSubsCertsApproved", title: "HVAC Quote/Subs/Certs Approved", type: "date" },
  { key: "proposalSentToSalesRep", title: "Proposal Sent To Sales Rep", type: "date" },
  { key: "estimatedSaleDate", title: "Estimated Sale Date", type: "date" },
  { key: "soldQuarter", title: "Sold Quarter", type: "number" },
  { key: "soldMonth", title: "Sold Month", type: "number" },
  { key: "soldYear", title: "Sold Year", type: "number" },
  { key: "reviewedSignedContractDocuments", title: "Reviewed Signed Contract Documents", type: "date" },
  { key: "submittedToUtilityForApproval", title: "Submitted to Utility for Approval", type: "date" },
  { key: "submittedForFinancingApproval", title: "Submitted for Financial Approval", type: "date" },
  { key: "financingApproved", title: "Financing Approved", type: "date" },
  { key: "utilityPreInspectionDate", title: "Utility Pre-Inspection Date", type: "date" },
  { key: "utilityPreInspectionDone", title: "Utility Pre-Inspection Done", type: "date" },
  { key: "utilityApprovalLetter", title: "Utility Approval Letter", type: "date" },
  { key: "ownerName", title: "Owner Name", type: "text" },
  { key: "ownerEmail", title: "Owner Email", type: "text" },
  { key: "ownerPhone", title: "Owner Phone", type: "phone" },
  { key: "onsiteAuditContact", title: "Onsite Audit Contact", type: "text" },
  { key: "onsiteEmail", title: "Onsite Email", type: "text" },
  { key: "onsitePhone", title: "Onsite Phone", type: "phone" },
  { key: "customerWelcomeLetterSent", title: "Customer Welcome Letter Sent", type: "date" },
  { key: "mechanicalPOSent", title: "Mechanical PO Sent", type: "date" },
  { key: "lightingLaborPOSent", title: "Ltg. Labor PO Sent", type: "date" },
  { key: "lightingMaterialPOSent", title: "Ltg. Material PO Sent", type: "date" },
  { key: "refrigerationPOSent", title: "Ref PO Sent", type: "date" },
  { key: "refrigerationETA", title: "Refrigeration ETA", type: "date" },
  { key: "hvacETA", title: "HVAC ETA", type: "date" },
  { key: "lightingETA", title: "Ltg. ETA", type: "date" },
  { key: "refrigerationInstalled", title: "Ref Installed", type: "date" },
  { key: "hvacInstalled", title: "HVAC Installed", type: "date" },
  { key: "lightingInstalled", title: "Ltg. Installed", type: "date" },
  { key: "lightingMaterialPOAmount", title: "Ltg Material PO Amount", type: "currency" },
  { key: "lightingLaborPOAmount", title: "Ltg Labor PO Amount", type: "currency" },
  { key: "mechanicalPOAmount", title: "Mechanical PO Amount", type: "currency" },
  { key: "refrigerationPOAmount", title: "Refrigeration PO Amount", type: "currency" },
  { key: "totalProjectInstalledDate", title: "Total Project Installed", type: "date" },
  { key: "totalProjectCompletedDate", title: "Total Project Completed", type: "date" },
  { key: "closeoutPackageReceived", title: "Closeout Package Received", type: "date" },
  { key: "closeoutPackageReviewed", title: "Closeout Package Reviewed", type: "date" },
  { key: "completionDocumentSentToCustomer", title: "Completion Document Sent to Customer", type: "date" },
  { key: "closeoutPackageSubmittedToUtility", title: "Closeout Package Submitted to Utility", type: "date" },
  { key: "utilityPostInspectionScheduleDate", title: "Utility Post Inspection Schedule Date", type: "date" },
  { key: "utilityPostInspectionCompleteDate", title: "Utility Post Inspection Complete Date", type: "date" },
  { key: "billedDepositDate", title: "Billed Deposit Date", type: "date" },
  { key: "billedDepositAmount", title: "Billed Deposit Amount", type: "currency" },
  { key: "depositReceivedDate", title: "Deposit Received Date", type: "date" },
  { key: "depositClearedDate", title: "Deposit Cleared Date", type: "date" },
  { key: "depositReceivedAmount", title: "Deposit Received Amount", type: "currency" },
  { key: "billedIncentiveDate", title: "Billed Incentive Date", type: "date" },
  { key: "billedIncentiveAmount", title: "Billed Incentive Amount", type: "currency" },
  { key: "billedIncentiveReceivedDate", title: "Billed Incentive Received Date", type: "date" },
  { key: "billedIncentiveReceivedAmount", title: "Billed Incentive Received Amount", type: "currency" },
  { key: "billedFinancedPortionDate", title: "Billed Financed Portion Date", type: "date" },
  { key: "billedFinancedPortionAmount", title: "Billed Financed Portion Amount", type: "currency" },
  { key: "financedPortionReceivedDate", title: "Financed Portion Received Date", type: "date" },
  { key: "financedPortionReceivedAmount", title: "Financed Portion Received Amount", type: "currency" },
  { key: "overageCustomerPortionDate", title: "Overage Customer Portion Date", type: "date" },
  { key: "overageCustomerPortionAmount", title: "Overage Customer Portion Amount", type: "currency" },
  { key: "overageCustomerPortionReceivedDate", title: "Overage Customer Portion Received Date", type: "date" },
  { key: "overageCustomerPortionReceivedAmount", title: "Overage Customer Portion Received Amount", type: "currency" },
  { key: "accrualDate", title: "Accrual Date", type: "date" },
  { key: "salesConfigurationId", title: "Sales Configuration", type: "text" },
  { key: "accountManagerCommissionAmount", title: "Account Manager Commission Amount", type: "currency" },
  { key: "leadGeneratorCommissionAmount", title: "Lead Generator Commission Amount", type: "currency" },
  { key: "salesSupportCommissionAmount", title: "Sales Support Commission Amount", type: "currency" },
  { key: "commissionPaid", title: "Commission Paid", type: "date" },
  { key: "percentToClose", title: "% to Close", type: "text" },
  { key: "lightingSubcontractorId", title: "Lighting Sub", type: "text" },
  { key: "hvacSubcontractorId", title: "HVAC Sub", type: "text" },
  { key: "refrigerationSubcontractorId", title: "Ref Sub", type: "text" },
  { key: "neifId", title: "NEIF ID", type: "text" },
  { key: "soldTotalProjectValue", title: "Sold Total Project Value", type: "currency" },
  { key: "soldTotalIncentive", title: "Sold Total Incentive", type: "currency" },
  { key: "soldTotalCustomerPortion", title: "Sold Total Customer Portion", type: "currency" },
  { key: "soldLtgValue", title: "Sold LTG Value", type: "currency" },
  { key: "soldHvacValue", title: "Sold HVAC Value", type: "currency" },
  { key: "soldRefrigerationValue", title: "Sold Refrigeration Value", type: "currency" },
  { key: "soldBudgetCosts", title: "Sold Budget Costs", type: "currency" },
  { key: "soldProfit", title: "Sold Profit", type: "currency" },
  { key: "soldMargin", title: "Sold Margin", type: "number" },
  { key: "financeLender", title: "Finance Lender", type: "text" },
  { key: "preFunding", title: "PreFunding", type: "boolean" },
  { key: "deferment", title: "Deferment", type: "text" },
  { key: "fiftyPercentPrefundInvoiceDate", title: "50% Prefund Invoice Date", type: "date" },
  { key: "twentyFivePercentPrefundInvoiceDate", title: "25% Prefund Invoice Date", type: "date" },
];

const SORTABLE_TYPES: ProjectColumnRenderType[] = [
  "text",
  "upper",
  "date",
  "currency",
  "number",
];

// Explicit per-type widths so the table sizes to its content and scrolls
// horizontally when the chosen columns overflow the viewport.
const WIDTH_BY_TYPE: Record<ProjectColumnRenderType, number> = {
  text: 170,
  upper: 120,
  phone: 150,
  list: 190,
  date: 130,
  currency: 150,
  number: 120,
  boolean: 110,
  status: 110,
  stage: 150,
  utility: 130,
  note: 230,
};

const get = (record: IProject, key: keyof IProject) => record[key];

const buildSorter = (def: ProjectColumnDef) => {
  if (!SORTABLE_TYPES.includes(def.type)) return undefined;
  const { key, type } = def;
  if (type === "date") {
    return (a: IProject, b: IProject) =>
      new Date((get(a, key) as Date) ?? 0).getTime() -
      new Date((get(b, key) as Date) ?? 0).getTime();
  }
  if (type === "currency" || type === "number") {
    return (a: IProject, b: IProject) =>
      Number(get(a, key) ?? 0) - Number(get(b, key) ?? 0);
  }
  return (a: IProject, b: IProject) =>
    String(get(a, key) ?? "").localeCompare(String(get(b, key) ?? ""));
};

const buildRender = (
  def: ProjectColumnDef,
  utilityProgramOptions?: ISelectOption[]
): ColumnType<IProject>["render"] => {
  switch (def.type) {
    case "upper":
      return (value) =>
        utilityService.truncateField(value?.toUpperCase());
    case "phone":
      return (value) =>
        value ? utilityService.phoneNumberFormatter(value) : "";
    case "list":
      return (value: string[]) =>
        utilityService.truncateField((value ?? []).join(", "));
    case "date":
      return (value) => (value ? dayjs(value).format(DateFormat) : "");
    case "currency":
      return (value) =>
        value === undefined || value === null
          ? ""
          : `$${utilityService.statisticsFormatter(value)}`;
    case "number":
      return (value) =>
        value === undefined || value === null
          ? ""
          : utilityService.statisticsFormatter(value);
    case "boolean":
      return (value) =>
        value === undefined || value === null ? "" : value ? "Yes" : "No";
    case "status":
      return (value) =>
        value ? (
          <Tag color={getStatusColor(value)}>
            {value.charAt(0).toUpperCase() + value.slice(1)}
          </Tag>
        ) : null;
    case "stage":
      return (value) => {
        const stage = ProjectStages.find(
          (s: ISelectOption) =>
            s.value?.toString().toLowerCase() === value?.toLowerCase()
        );
        return utilityService.truncateField(
          stage?.label?.toString() ?? value ?? ""
        );
      };
    case "utility":
      return (value) => {
        const program = (utilityProgramOptions || []).find(
          (p: ISelectOption) =>
            p.value?.toString().toLowerCase() === value?.toLowerCase()
        );
        return utilityService.truncateField(
          program?.label?.toString() ?? value ?? ""
        );
      };
    case "note":
      return (value) => (
        <div
          dangerouslySetInnerHTML={{
            __html: utilityService.truncateHtml(value?.content as string),
          }}
        />
      );
    case "text":
    default:
      return (value) => utilityService.truncateField(value);
  }
};

export const getProjectColumns = (
  isSubcontractor?: boolean,
  utilityProgramOptions?: ISelectOption[]
): ColumnType<IProject>[] => {
  return PROJECT_COLUMN_DEFS.map((def) => ({
    title: def.title,
    key: def.key,
    dataIndex: def.key,
    width: WIDTH_BY_TYPE[def.type],
    ellipsis: true,
    sorter: buildSorter(def),
    render: buildRender(def, utilityProgramOptions),
    // The latest note is not relevant to subcontractors.
    hidden: def.key === "latestProjectNote" ? isSubcontractor : undefined,
  }));
};

/**
 * Filters a column set to those visible to the user. Columns hidden by role
 * (`hidden: true`) are always dropped; on top of that the user's own
 * `hiddenKeys` (persisted preference) are removed. When no keys are hidden,
 * every role-accessible column is returned. Canonical column order is preserved.
 */
export const getVisibleProjectColumns = (
  columns: ColumnType<IProject>[],
  hiddenKeys?: string[]
): ColumnType<IProject>[] => {
  const accessible = columns.filter((column) => !column.hidden);

  if (!hiddenKeys?.length) {
    return accessible;
  }

  return accessible.filter(
    (column) => !hiddenKeys.includes(String(column.key))
  );
};
